import { c as _c } from "react/compiler-runtime";
import Link from 'next/link';
import Image from 'next/image';
import type { LinkProps } from 'next/link';
import { Icon } from '~/components/primitives';
import ButtonLink from '~/components/primitives/Button/ButtonLink';
import { cn } from '~/utils';

/* ---------------------------------------------------------------------------
 * HeaderHovercard — shadcn-style compound component for header dropdown cards
 * Used by both the cart and wishlist header hovercards.
 * -------------------------------------------------------------------------*/

function HeaderHovercardRoot(t0) {
  const $ = _c(2);
  const {
    children
  } = t0;
  let t1;
  if ($[0] !== children) {
    t1 = <>{children}</>;
    $[0] = children;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
}
function HeaderHovercardTitle(t0) {
  const $ = _c(11);
  const {
    children,
    className,
    href
  } = t0;
  if (href) {
    let t1;
    if ($[0] !== className) {
      t1 = cn("mb-3 inline-flex text-sm font-bold text-primary transition-colors hover:text-orange-500", className);
      $[0] = className;
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    let t2;
    if ($[2] !== children || $[3] !== href || $[4] !== t1) {
      t2 = <Link href={href} className={t1}>{children}</Link>;
      $[2] = children;
      $[3] = href;
      $[4] = t1;
      $[5] = t2;
    } else {
      t2 = $[5];
    }
    return t2;
  }
  let t1;
  if ($[6] !== className) {
    t1 = cn("mb-3 text-sm font-bold text-primary", className);
    $[6] = className;
    $[7] = t1;
  } else {
    t1 = $[7];
  }
  let t2;
  if ($[8] !== children || $[9] !== t1) {
    t2 = <p className={t1}>{children}</p>;
    $[8] = children;
    $[9] = t1;
    $[10] = t2;
  } else {
    t2 = $[10];
  }
  return t2;
}
function HeaderHovercardList(t0) {
  const $ = _c(5);
  const {
    children,
    className
  } = t0;
  let t1;
  if ($[0] !== className) {
    t1 = cn("max-h-64 space-y-1 overflow-y-auto", className);
    $[0] = className;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== children || $[3] !== t1) {
    t2 = <ul className={t1}>{children}</ul>;
    $[2] = children;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  return t2;
}
function HeaderHovercardCTA(t0) {
  const $ = _c(6);
  const {
    href,
    children,
    className
  } = t0;
  let t1;
  if ($[0] !== className) {
    t1 = cn("mt-3 w-full rounded-lg px-4 py-2", className);
    $[0] = className;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== children || $[3] !== href || $[4] !== t1) {
    t2 = <ButtonLink href={href} size="xs" className={t1}>{children}</ButtonLink>;
    $[2] = children;
    $[3] = href;
    $[4] = t1;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  return t2;
}
function HeaderHovercardEmpty(t0) {
  const $ = _c(11);
  const {
    icon,
    message,
    ctaLabel,
    ctaHref
  } = t0;
  let t1;
  if ($[0] !== icon) {
    t1 = <Icon name={icon} className="text-blue-300" size="md" />;
    $[0] = icon;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== message) {
    t2 = <p className="text-sm text-blue-600">{message}</p>;
    $[2] = message;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== ctaHref || $[5] !== ctaLabel) {
    t3 = <Link href={ctaHref} className="text-sm font-semibold text-orange-500 hover:text-orange-700">{ctaLabel}</Link>;
    $[4] = ctaHref;
    $[5] = ctaLabel;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== t1 || $[8] !== t2 || $[9] !== t3) {
    t4 = <div className="flex flex-col items-center gap-3 py-4 text-center">{t1}{t2}{t3}</div>;
    $[7] = t1;
    $[8] = t2;
    $[9] = t3;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  return t4;
}
function HeaderHovercardItem(t0) {
  const $ = _c(8);
  const {
    href,
    children,
    className
  } = t0;
  let base;
  let t1;
  if ($[0] !== children || $[1] !== className || $[2] !== href) {
    t1 = Symbol.for("react.early_return_sentinel");
    bb0: {
      base = cn("flex items-center gap-3 rounded-lg px-1 py-1", className);
      if (href) {
        t1 = <a href={href} className={cn(base, "transition-colors hover:bg-blue-50")}>{children}</a>;
        break bb0;
      }
    }
    $[0] = children;
    $[1] = className;
    $[2] = href;
    $[3] = base;
    $[4] = t1;
  } else {
    base = $[3];
    t1 = $[4];
  }
  if (t1 !== Symbol.for("react.early_return_sentinel")) {
    return t1;
  }
  let t2;
  if ($[5] !== base || $[6] !== children) {
    t2 = <div className={base}>{children}</div>;
    $[5] = base;
    $[6] = children;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
}
function HeaderHovercardItemImage(t0) {
  const $ = _c(8);
  const {
    src,
    alt,
    className
  } = t0;
  let t1;
  if ($[0] !== className) {
    t1 = cn("relative h-12 w-16 shrink-0 overflow-hidden rounded-md bg-blue-100", className);
    $[0] = className;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== alt || $[3] !== src) {
    t2 = src && <Image src={src} alt={alt} fill={true} className="object-cover" />;
    $[2] = alt;
    $[3] = src;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== t1 || $[6] !== t2) {
    t3 = <div className={t1}>{t2}</div>;
    $[5] = t1;
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  return t3;
}

/* ---------------------------------------------------------------------------
 * Public API — follows shadcn compound component pattern
 * -------------------------------------------------------------------------*/

export { HeaderHovercardRoot as HeaderHovercard, HeaderHovercardTitle, HeaderHovercardList, HeaderHovercardCTA, HeaderHovercardEmpty, HeaderHovercardItem, HeaderHovercardItemImage };